import { FC, useCallback, useMemo, Fragment } from 'react';
import { ServiceCategorySection } from '../ServiceCategorySection/ServiceCategorySection';
import { ServiceTopCard } from '../ServiceTopCard/ServiceTopCard';
import style from './servicesAllStyle.module.css';
import IconCategoryCar from '@/assets/images/menu/icon-category-car.svg';
import IconCategoryMaintenance from '@/assets/images/menu/icon-category-maintenance.svg';
import IconCategoryRentCar from '@/assets/images/menu/icon-category-rent-car.svg';
import IconCategoryShop from '@/assets/images/menu/icon-category-shop.svg';
import { isApp } from '@/config/device';
import { useFetchBanners } from '@/hooks/api/banner/useFetchBanners';
import { useFetchUserActionCategories } from '@/hooks/api/service/useFetchUserActionCategories';
import { useEffectiveAffiliateId } from '@/hooks/useEffectiveAffiliateId';

// ナビボタン用アイコンマッピング（固定）
const CATEGORY_ICON_MAP: Record<string, string> = {
  '売却・購入する': IconCategoryCar,
  メンテナンスする: IconCategoryMaintenance,
  買う: IconCategoryShop,
  借りる: IconCategoryRentCar,
};

// ナビボタン用短縮名（固定）
const CATEGORY_SHORT_NAME: Record<string, string> = {
  '売却・購入する': '売却・購入',
  メンテナンスする: 'メンテナンス',
};

export const ServicesAll: FC = () => {
  // 遷移元のホーム画面と同じ「ショッピング先」の法人で出し分ける
  const affiliateId = useEffectiveAffiliateId();

  const { userActionCategories } = useFetchUserActionCategories({
    m_affiliate_id: affiliateId,
  });

  const { banners } = useFetchBanners({
    m_affiliate_id: affiliateId,
  });

  // パートナー企業: partner_name が設定されているバナーをフィルタ
  const partnerBanners = useMemo(
    () => banners?.filter((b) => b.partner_name) ?? [],
    [banners],
  );

  const handleCategoryClick = useCallback((categoryId: number) => {
    const element = document.getElementById(`category-section-${categoryId}`);
    element?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handlePartnerClick = useCallback((url: string) => {
    if (isApp) {
      window.open(url + 'path_type=2', '_blank');
    } else {
      window.open(url, '_blank');
    }
  }, []);

  return (
    <Fragment>
      {/* 上部のカテゴリーカード（1枚SVG画像） */}
      <ServiceTopCard />

      {/* カテゴリーナビゲーションボタン */}
      {userActionCategories && userActionCategories.length > 0 && (
        <div className={style['category-nav']}>
          {userActionCategories.map((category) => (
            <button
              key={category.id}
              className={style['category-nav-button']}
              onClick={() => handleCategoryClick(category.id)}
            >
              <img
                src={CATEGORY_ICON_MAP[category.name]}
                alt=""
                className={style['nav-icon']}
              />
              <div className={style['nav-label']}>
                <span>
                  {CATEGORY_SHORT_NAME[category.name] || category.name}
                </span>
                <span className={style['nav-arrow']}>∨</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* カテゴリー別サービス一覧 */}
      {userActionCategories?.map((category) => (
        <ServiceCategorySection
          key={category.id}
          category={category}
          id={`category-section-${category.id}`}
        />
      ))}

      {/* パートナー企業セクション */}
      {partnerBanners.length > 0 && (
        <div className={style['partner-section']}>
          <div className={style['partner-title']}>パートナー企業</div>
          <div className={style['partner-grid']}>
            {partnerBanners.map((banner, index) => (
              <div
                key={index}
                className={style['partner-card']}
                onClick={() =>
                  banner.partner_url && handlePartnerClick(banner.partner_url)
                }
              >
                {banner.partner_logo_path && (
                  <img
                    className={style['partner-image']}
                    src={banner.partner_logo_path}
                    alt={banner.partner_name || ''}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Fragment>
  );
};
